/* ===================================================================
   Text opt-ins -- who can be texted, who can't, and exactly why.

   Penn Condition 1, front half (2026-07-29). This screen exists because the
   rule is genuinely surprising to a business owner: having someone's mobile
   number is NOT permission to text them. Two separate things have to happen.

     1. The customer asks. They tick the optional box at the job, on the intake
        form, or on the website. The owner records that here.
     2. The customer confirms. Rhody Reviews sends ONE text asking them to
        reply YES. Their YES is the permission of record. No reply means no
        further messages, ever.

   That is what was registered with the phone carriers, so it is what the
   product does. This screen's whole job is to make it legible rather than
   mysterious -- an owner who cannot see WHY a customer is un-textable will
   assume the product is broken and go looking for a bulk button.

   READABILITY OVERHAUL (Brad, 2026-08-08). Steve used this screen on a phone
   and it was painful: too wordy, type too small, rows too tight. It is now the
   REFERENCE IMPLEMENTATION of the app-wide Readability Standard
   (styles/READABILITY_STANDARD.md + styles/readability.css + the --rd-* tokens):
   bigger, bolder type; high contrast; far fewer, plainer words; and a self-drawn
   status ICON paired with a word on every row, so an owner reads status at a
   glance without reading a sentence. The two-step "they ask, then they confirm"
   meaning is preserved -- it is compliance-load-bearing -- just said plainly.

   NO DEV ARTIFACTS. Every stage label and explanation is authored server-side
   in compliance/sms_optin_capture.py and rendered verbatim. This file contains
   no error codes, no webhook URLs, no keys, no JSON dumps.

   NO BULK ACTION. There is no "confirm everyone" control, because there is no
   such route. A list cannot vouch for who ticked the box. (Bulk consent is
   handled separately at customer-upload time.)

   LIVE DATA ONLY: everything comes from useSmsOptIn(). An account with no
   customers says so. An account where nobody has asked yet says that, plainly,
   rather than dressing up a zero.
   =================================================================== */
import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Card, CardHead, EmptyState } from '../components/ui';
import { useSmsOptIn, useSmsOptInActions } from '../hooks';
import type { OptInEntry, OptInSourceChoice, OptInStage } from '../lib/api';
import { ApiError } from '../lib/api';
import '../styles/components.css';
import '../styles/readability.css';

/* ---------------- status icons (self-drawn, shape carries meaning) ----
   One shape per meaning, paired with a word and a tone. Shape first, word
   second, color third -- a colorblind owner, or one in glare, still reads it. */
const IcoCheck = (
  <path d="M5 12l5 5L20 6" strokeLinecap="round" strokeLinejoin="round" />
);
const IcoPlane = (
  <path d="M21 3L10 14M21 3l-7 18-4-8-8-4 19-6z" strokeLinecap="round" strokeLinejoin="round" />
);
const IcoClock = (
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3.5 2" strokeLinecap="round" strokeLinejoin="round" />
  </>
);
const IcoDash = (
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M8.5 12h7" strokeLinecap="round" />
  </>
);
const IcoNoEntry = (
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M6 12h12" strokeLinecap="round" />
  </>
);
const IcoPhoneOff = (
  <>
    <path d="M6 3h3l2 5-2.5 1.5a11 11 0 005 5L16 12l5 2v3a2 2 0 01-2 2A15 15 0 014 6a2 2 0 012-3z" strokeLinejoin="round" />
    <path d="M3 3l18 18" strokeLinecap="round" />
  </>
);

type Tone = 'green' | 'gold' | 'blue' | 'red' | 'gray';

/* Stage -> how it reads at a glance: an icon shape + a tone. The WORDS come
   from the server headline (verbatim), so this map only chooses the picture.
   Red is reserved for the closed "stop" states and stays rare on purpose. */
const STAGE_VIEW: Record<OptInStage, { tone: Tone; icon: ReactNode }> = {
  confirmed: { tone: 'green', icon: IcoCheck },
  ready_to_confirm: { tone: 'blue', icon: IcoPlane },
  awaiting_reply: { tone: 'gold', icon: IcoClock },
  expired: { tone: 'gold', icon: IcoClock },
  no_opt_in_on_file: { tone: 'gray', icon: IcoDash },
  no_reply: { tone: 'gray', icon: IcoNoEntry },
  opted_out: { tone: 'red', icon: IcoNoEntry },
  do_not_contact: { tone: 'red', icon: IcoNoEntry },
  no_phone: { tone: 'gray', icon: IcoPhoneOff },
  invalid_phone: { tone: 'gray', icon: IcoPhoneOff },
};

function StatusPill({ stage, label }: { stage: OptInStage; label: string }) {
  const v = STAGE_VIEW[stage];
  return (
    <span className={'rd-status rd-tone-' + v.tone}>
      <span className="rd-status__ico" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4}>
          {v.icon}
        </svg>
      </span>
      <span className="rd-status__label">{label}</span>
    </span>
  );
}

/* Worst-to-best is the wrong sort for an owner. Put the rows they can ACT on
   at the top, then the ones waiting on the customer, then the rest. */
const SORT_WEIGHT: Record<OptInStage, number> = {
  ready_to_confirm: 0,
  no_opt_in_on_file: 1,
  awaiting_reply: 2,
  expired: 3,
  confirmed: 4,
  no_reply: 5,
  invalid_phone: 6,
  no_phone: 7,
  do_not_contact: 8,
  opted_out: 9,
};

function fmtDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function errText(e: unknown): string {
  if (e instanceof ApiError && e.detail) return e.detail;
  return 'Something went wrong. Nothing was sent or saved.';
}

/* ---------------- One customer row ---------------- */
function OptInRow({
  entry,
  sources,
  onRecord,
  onClear,
  onSend,
  busy,
}: {
  entry: OptInEntry;
  sources: OptInSourceChoice[];
  onRecord: (source: string, note: string) => Promise<void>;
  onClear: () => Promise<void>;
  onSend: () => Promise<void>;
  busy: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState(sources[0]?.value ?? 'point_of_service');
  const [note, setNote] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const canRecord =
    !entry.opt_in.captured &&
    entry.stage !== 'opted_out' &&
    entry.stage !== 'do_not_contact' &&
    entry.stage !== 'no_phone' &&
    entry.stage !== 'invalid_phone';

  async function submit() {
    setErr(null);
    try {
      await onRecord(source, note);
      setOpen(false);
      setNote('');
      setConfirmed(false);
    } catch (e) {
      setErr(errText(e));
    }
  }

  async function run(fn: () => Promise<void>) {
    setErr(null);
    try {
      await fn();
    } catch (e) {
      setErr(errText(e));
    }
  }

  return (
    <div className="rd-row">
      <div className="rd-row__main">
        <div className="rd-name">{entry.customer.name || 'Unnamed customer'}</div>
        <StatusPill stage={entry.stage} label={entry.headline} />
        {entry.customer.phone && <div className="rd-phone">{entry.customer.phone}</div>}
        <p className="rd-body">{entry.explanation}</p>

        {entry.opt_in.captured && (
          <p className="rd-meta">
            Recorded {fmtDate(entry.opt_in.at)}
            {entry.opt_in.source_label ? ` - ${entry.opt_in.source_label}` : ''}
            {entry.opt_in.recorded_by ? ` - by ${entry.opt_in.recorded_by}` : ''}
            {entry.opt_in.note ? ` - "${entry.opt_in.note}"` : ''}
          </p>
        )}

        {err && <p className="rd-err">{err}</p>}
      </div>

      <div className="rd-actions">
        {entry.may_send_confirmation && (
          <button className="rd-btn rd-btn--primary" disabled={busy} onClick={() => void run(onSend)}>
            Send the yes text
          </button>
        )}
        {canRecord && !open && (
          <button className="rd-btn" disabled={busy} onClick={() => setOpen(true)}>
            They asked
          </button>
        )}
        {entry.opt_in.captured && (
          <button className="rd-btn" disabled={busy} onClick={() => void run(onClear)}>
            Remove
          </button>
        )}
      </div>

      {open && (
        <div className="rd-form">
          <label className="rd-field">
            <span>Where did they ask?</span>
            <select value={source} onChange={(e) => setSource(e.target.value)}>
              {sources.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
          <label className="rd-field">
            <span>A note (optional)</span>
            <input
              type="text"
              value={note}
              maxLength={300}
              placeholder="Job number, form name, anything that helps"
              onChange={(e) => setNote(e.target.value)}
            />
          </label>
          <label className="rd-check">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
            />
            <span>This customer asked me to text them. I will not check this for anyone who did not ask.</span>
          </label>
          <div className="rd-actions">
            <button
              className="rd-btn rd-btn--primary"
              disabled={!confirmed || busy}
              onClick={() => void submit()}
            >
              Save
            </button>
            <button
              className="rd-btn"
              disabled={busy}
              onClick={() => {
                setOpen(false);
                setErr(null);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Screen ---------------- */
export function TextOptIns() {
  const { data, isLoading, isError, refetch } = useSmsOptIn();
  const { record, sendConfirmation, recording, sending } = useSmsOptInActions();
  const [preview, setPreview] = useState<{ body: string; sent: boolean; name: string } | null>(
    null,
  );

  const rows = useMemo(() => {
    const list = data?.customers ?? [];
    return [...list].sort((a, b) => {
      const w = SORT_WEIGHT[a.stage] - SORT_WEIGHT[b.stage];
      if (w !== 0) return w;
      return (a.customer.name ?? '').localeCompare(b.customer.name ?? '');
    });
  }, [data]);

  if (isLoading) {
    return (
      <Card>
        <CardHead title="Text opt-ins" />
        <p className="rd-lede">Loading your customers.</p>
      </Card>
    );
  }

  if (isError || !data) {
    return (
      <Card>
        <CardHead title="Text opt-ins" />
        <p className="rd-lede">We could not load your text opt-in status just now.</p>
        <button className="rd-btn" onClick={() => void refetch()}>
          Try again
        </button>
      </Card>
    );
  }

  const s = data.summary;
  const busy = recording || sending;

  return (
    <div className="rd-screen">
      <Card>
        <CardHead title="How texting works" />
        <p className="rd-lede">
          A phone number is not a yes. Before we text a customer, two things
          have to happen, in this order.
        </p>
        <ol className="rd-steps">
          <li className="rd-step">
            <span className="rd-step__n">1</span>
            <span className="rd-step__t">
              <b>They ask.</b> The customer checks a box at the job, on your
              form, or on your website. You mark it here.
            </span>
          </li>
          <li className="rd-step">
            <span className="rd-step__n">2</span>
            <span className="rd-step__t">
              <b>They say yes.</b> We send one text. They reply YES. That YES is
              their permission. No reply means we never text them.
            </span>
          </li>
        </ol>
        <p className="rd-note">
          This is the rule the phone carriers hold us to. That is why there is
          no button to text your whole list. Email is not affected.
        </p>
      </Card>

      {!data.account.sms_authorized && (
        <Card>
          <p className="rd-banner">
            <b>Texting is not on for your account yet.</b> You can mark opt-ins
            now. Nothing will send until it is on.
          </p>
        </Card>
      )}

      {data.account.sms_authorized && !data.account.texting_live && (
        <Card>
          <p className="rd-banner">
            <b>Texting is not live yet.</b> You can mark opt-ins and see the
            message. Nothing will actually send.
          </p>
        </Card>
      )}

      <Card>
        <CardHead title="Where each customer stands" />
        <div className="rd-tiles">
          {(
            [
              ['confirmed', 'Can text', s.confirmed],
              ['ready_to_confirm', 'Ready to send', s.ready_to_confirm],
              ['awaiting_reply', 'Waiting on yes', s.awaiting_reply],
              ['no_opt_in_on_file', 'Not asked yet', s.no_opt_in_on_file],
            ] as [OptInStage, string, number][]
          ).map(([stage, label, value]) => (
            <div key={stage} className="rd-tile">
              <div className="rd-tile__top">
                <StatusPill stage={stage} label={label} />
              </div>
              <div className="rd-tile__num">{value}</div>
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <CardHead title={`Customers (${s.total})`} />
        {rows.length === 0 ? (
          <EmptyState
            title="No customers yet"
            desc="Once you add or import customers they'll show up here with their texting status."
          />
        ) : s.confirmed === 0 && s.ready_to_confirm === 0 && s.awaiting_reply === 0 ? (
          <>
            <p className="rd-lede">
              No one has asked for texts yet, so no one can be texted. That is
              normal for a new list. Start checking the box with customers at the
              job, then mark it here.
            </p>
            {rows.map((entry) => (
              <OptInRow
                key={entry.customer.id}
                entry={entry}
                sources={data.opt_in_sources}
                busy={busy}
                onRecord={async (source, note) => {
                  await record(entry.customer.id, {
                    opted_in: true,
                    confirm: true,
                    source,
                    note,
                  });
                }}
                onClear={async () => {
                  await record(entry.customer.id, { opted_in: false });
                }}
                onSend={async () => {
                  const res = await sendConfirmation(entry.customer.id);
                  setPreview({
                    body: res.body,
                    sent: res.sent,
                    name: entry.customer.name ?? 'this customer',
                  });
                }}
              />
            ))}
          </>
        ) : (
          rows.map((entry) => (
            <OptInRow
              key={entry.customer.id}
              entry={entry}
              sources={data.opt_in_sources}
              busy={busy}
              onRecord={async (source, note) => {
                await record(entry.customer.id, {
                  opted_in: true,
                  confirm: true,
                  source,
                  note,
                });
              }}
              onClear={async () => {
                await record(entry.customer.id, { opted_in: false });
              }}
              onSend={async () => {
                const res = await sendConfirmation(entry.customer.id);
                setPreview({
                  body: res.body,
                  sent: res.sent,
                  name: entry.customer.name ?? 'this customer',
                });
              }}
            />
          ))
        )}
      </Card>

      {preview && (
        <Card>
          <CardHead
            title={preview.sent ? 'Text sent' : 'Nothing sent'}
            link="Close"
            onLink={() => setPreview(null)}
          />
          <p className="rd-lede">
            {preview.sent
              ? `This went to ${preview.name}. If they reply YES, review texts can start. No reply means nothing else is sent.`
              : `Texting is not live yet, so nothing went to ${preview.name}. This is the exact text that would go out.`}
          </p>
          <blockquote className="rd-quote">{preview.body}</blockquote>
        </Card>
      )}
    </div>
  );
}

export default TextOptIns;
